import { Time } from "@internationalized/date";
import { useTranslations } from "next-intl";
import { Image as DatoImage } from "react-datocms/image";

import { useTimeFormatter } from "@trip/react-hooks";

import { ExternalLinkIcon } from "~/components/icons/external-link";
import { LoadingSpinner } from "~/components/loading-spinner";
import { H3 } from "~/components/typography";
import { Button } from "~/components/ui/button";
import { type ProductId } from "~/lib/tours";
import { useCart } from "~/lib/use-cart";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { useTripTourCartLine } from "~/lib/use-trip-tour";
import { Link } from "~/navigation";
import { type RouterOutputs } from "~/trpc/types";

import { type Day } from "../_lib/trip";

type TourChoice = RouterOutputs["tours"]["choices"][0];
type DatoTour = Day["tours"][0]["tour"];

export function TourInfo({
  datoTour,
  choice,
  isLoading,
  datoDayId,
  setPage,
  productId,
}: {
  datoTour: DatoTour;
  choice?: TourChoice;
  isLoading: boolean;
  datoDayId: string;
  productId: ProductId;
  setPage?: (value: "booking") => void;
}) {
  const t = useTranslations("TripDetails");
  const tCommon = useTranslations("Common");
  const formatCurrency = useCurrencyFormatter();
  const formatTime = useTimeFormatter();
  const cartLine = useTripTourCartLine({
    datoDayId,
    datoTourId: datoTour.id,
  });
  const { removeTourCartLine } = useCart();
  return (
    <div className="flex h-full flex-col gap-4">
      <div className="relative aspect-video overflow-hidden rounded-lg md:hidden">
        <DatoImage
          data={datoTour.heroImage.responsiveImage}
          layout="fill"
          objectFit="cover"
        />
        <div className="absolute inset-0 rounded-lg shadow-[inset_0_0_0_1px_rgba(0,0,0,.1)]" />
      </div>
      <div>
        <H3 className="mb-2 line-clamp-3">{datoTour.title}</H3>
        <Link
          className="text-prune-500 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium"
          target="_blank"
          href={`/tours/${datoTour.slug}/${datoTour.id}`}
        >
          {t("learnMore")} <ExternalLinkIcon />
        </Link>
      </div>
      <div className="mt-2 grow space-y-4 text-sm text-neutral-500">
        <p className="line-clamp-6">{datoTour.summary}</p>
        {choice?.startTimes && choice.startTimes.length > 0 && !cartLine ? (
          <p className="text-neutral-900">
            {t("availableAt", {
              times: choice.startTimes
                .map((time) => formatTime(new Time(time.hour, time.minute)))
                .join(", "),
            })}
          </p>
        ) : null}
      </div>
      <div>
        {cartLine?.amount ? (
          <div>
            <div className="text-sm text-neutral-500 [&>strong]:font-medium">
              {t.rich("priceForPeople", {
                amount: (chunks) => <strong>{chunks}</strong>,
                count: cartLine.lineItem.passengers.reduce(
                  (agg, { count }) => agg + count,
                  0,
                ),
                formattedAmount: formatCurrency(cartLine.amount),
              })}
            </div>
            <div className="mt-0.5 font-medium">
              {cartLine.lineItem.startTime ? (
                <>
                  {t("tourStartingAtTime", {
                    time: formatTime(
                      new Time(
                        cartLine.lineItem.startTime.getUTCHours(),
                        cartLine.lineItem.startTime.getUTCMinutes(),
                      ),
                    ),
                  })}
                </>
              ) : (
                <>{t("tourAddedToItinerary")}</>
              )}
            </div>
            <button
              type="button"
              disabled={removeTourCartLine.isPending}
              className="text-prune-500 py-2 font-medium disabled:opacity-50"
              onClick={(event) => {
                event.preventDefault();
                void removeTourCartLine.mutateAsync({
                  lineItemId: cartLine.lineItem.id,
                  productId,
                });
              }}
            >
              {tCommon("remove")}
            </button>
          </div>
        ) : choice ? (
          <>
            <div className="mb-2 text-center text-xs text-neutral-500">
              <span className="font-medium">
                {formatCurrency(choice.amount)}
              </span>
              <span> {t("forPeopleCount", { count: 2 })}</span>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={(event) => {
                event.preventDefault();
                setPage?.("booking");
              }}
            >
              {t("addExperience")}
            </Button>
          </>
        ) : isLoading ? (
          <Button className="w-full" disabled={true} variant="outline">
            <LoadingSpinner />
          </Button>
        ) : (
          <Button variant="outline" className="w-full" disabled={true}>
            {t("notAvailable")}
          </Button>
        )}
      </div>
    </div>
  );
}
